import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  FormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { forkJoin } from 'rxjs';
import {
  COMMERCIAL_CONTACT_DETAIL_CONTAINER,
  COMMERCIAL_CONTACT_SEARCH_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  DELETE_COMMERCIAL_CONTACT,
  NEW_COMMERCIAL_CONTACT,
} from 'src/app/constants/commercial-constants';
import {
  getDirtyValues,
  toSelectItems,
} from 'src/app/core/functions/common-functions';
import {
  buildHistoryTabId,
  historyVersionSuffix,
  parseHistoryTabId,
} from 'src/app/core/functions/history-tab-functions';
import {
  VIEW_AUDIT,
  VIEW_HISTORICAL_RECORD,
} from 'src/app/constants/action-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { CommercialContact } from 'src/app/model/commercial-services/commercial-contact';
import { CommercialContactUpdateDto } from 'src/app/model/commercial-services/commercial-contact-update-dto';
import { CommercialContactApiService } from 'src/app/services/api/commercial-contact-api.service';
import { CommercialFacilityApiService } from 'src/app/services/api/commercial-facility-api.service';
import { CommercialContactBuilderService } from 'src/app/services/builder/commercial-contact-builder.service';
import { CommercialContactTableService } from 'src/app/services/table/commercial-contact-table.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { CommercialContactFormComponent } from '../../components/commercial-contact-form/commercial-contact-form.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { TranslatePipe } from '@ngx-translate/core';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import { SelectItem } from '@openng/optimus-ui/api';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { HistoryVersionDialogComponent } from 'src/app/core/containers/history-version-dialog/history-version-dialog.component';
import {
  CommercialContactMessageKeys,
  ValidationMessageEntry,
} from 'src/app/core/type-helpers/validation-msg';

@Component({
  selector: 'app-commercial-contact-details-container',
  templateUrl: './commercial-contact-details-container.component.html',
  styleUrls: ['./commercial-contact-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ButtonDirective,
    ScrollPanel,
    Select,
    CommercialContactFormComponent,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class CommercialContactDetailsContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _fb = inject(UntypedFormBuilder);
  protected _commercialContactApi = inject(CommercialContactApiService);
  protected _commercialFacilityApi = inject(CommercialFacilityApiService);
  protected _commercialContactBuilder = inject(CommercialContactBuilderService);
  protected _commercialContactTableService = inject(
    CommercialContactTableService
  );
  protected _logAndMessage = inject(LogAndMessageService);
  protected _authApi = inject(AuthApiService);
  protected _dialogService = inject(DialogService);

  readonly commercialContactForm =
    viewChild<CommercialContactFormComponent>('ccf');

  readonly contact = signal<CommercialContact | null>(null);
  readonly saving = signal(false);
  readonly canEdit = signal(false);
  readonly canCreate = signal(false);
  readonly availableJobRoles = signal<SelectItem[]>([]);
  readonly companyOptions = signal<SelectItem[]>([]);
  readonly actions = signal<SelectItem[]>([]);
  readonly selectedAction = signal<string | null>(null);
  readonly isHistoricalRecord = signal(false);
  readonly formState = signal<UntypedFormGroup | undefined>(undefined);
  readonly formDirty = signal(false);
  readonly isLoading = signal(true);
  validationMessages?: Record<
    CommercialContactMessageKeys,
    ValidationMessageEntry
  >;

  ngOnInit() {
    this.setupTabClosingSubscription();
    this.initialize();
  }

  initialize() {
    const validations =
      this._commercialContactBuilder.buildCommercialContactValidationMessages();
    this.validationMessages = validations.messages;

    const reference = parseHistoryTabId(this.id);
    const isHistoricalRecord = reference.version !== undefined;
    this.isHistoricalRecord.set(isHistoricalRecord);
    this.contact.set(
      isHistoricalRecord
        ? null
        : this._commercialContactTableService.getSelected()
    );
    this.canEdit.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.EDIT_COMMERCIAL_CONTACTS
      )
    );
    this.canCreate.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS
          .CREATE_COMMERCIAL_CONTACTS
      )
    );

    forkJoin({
      companies: this._commercialFacilityApi.getCommercialCompanies(),
      jobRoles: this._commercialContactApi.getCommercialJobRoles(),
    }).subscribe(({ companies, jobRoles }) => {
      const choose = this._translateService.instant('COMMON.LABEL.CHOOSE');
      const companyOptions = toSelectItems(companies, choose);
      this.availableCompanies = companyOptions;
      this.companyOptions.set(companyOptions);
      this.availableJobRoles.set(toSelectItems(jobRoles, choose));
    });

    if (this.contact()) {
      this.setupScreen();
    } else if (reference.id && reference.id !== NEW_COMMERCIAL_CONTACT) {
      this._commercialContactApi
        .getCommercialContact(reference.id, reference.version)
        .subscribe((contact) => {
          this.contact.set(contact);
          this.setupScreen();
        });
    } else {
      this.contact.set(
        this._commercialContactBuilder.buildNewCommercialContact()
      );

      const newLabel = this._translateService.instant(
        'COMMERCIAL_SERVICES.LABEL.NEW_COMMERCIAL_CONTACT'
      );
      this._tabService.updateActiveTabLabel(newLabel);

      this.buildForm();
      this.isLoading.set(false);
    }
  }

  setupScreen() {
    const contact = this.contact();
    if (!contact) {
      this._logAndMessage.errorLogOnly('Commercial contact object is null');
      const index = this._tabService.getActiveIndex();
      this._tabService.closeTab(index);
    } else {
      this.setTabLabel(contact);
    }

    this.updateActionsList();
    this.buildForm();
    this.isLoading.set(false);
  }

  private setTabLabel(contact: CommercialContact): void {
    const tab = this._tabService.getTabById(this.id);
    if (!tab) {
      return;
    }
    const label = this._translateService.instant(
      'COMMERCIAL_SERVICES.SCREEN.COMMERCIAL_CONTACT'
    );
    const suffix = historyVersionSuffix(parseHistoryTabId(this.id).version);
    this._tabService.updateTabLabel(
      tab,
      `${label}: ${contact.fullName}${suffix}`
    );
  }

  ngOnDestroy(): void {
    if (this.valueSub) {
      this.valueSub.unsubscribe();
    }
    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }
  }

  buildForm() {
    const form = this._commercialContactBuilder.buildForm(
      this._fb,
      this.contact()
    );
    this.form = form;
    this.formState.set(form);

    const writeAccess =
      !this.isHistoricalRecord() &&
      (this.contact()?.id ? this.canEdit() : this.canCreate());
    if (!writeAccess) {
      form.disable();
    }

    this.formDirty.set(form.dirty);
    this.valueSub = form.valueChanges.subscribe(() => {
      this.formDirty.set(form.dirty);
      if (form.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  saveContact() {
    if (
      this.isHistoricalRecord() ||
      (this.contact()?.id ? !this.canEdit() : !this.canCreate())
    ) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_HEADER',
        bodyKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_BODY',
        bodyParams: {
          action: this._translateService
            .instant('COMMON.LABEL.BUTTONS.SAVE')
            .toLowerCase(),
        },
      });
      return;
    }

    if (this.form!.valid) {
      this.saving.set(true);
      const raw = this.form!.getRawValue();
      const dv = getDirtyValues(this.form);

      if (!raw.id) {
        const create = this._commercialContactBuilder.buildCreateObject(raw);
        this.createContact(create);
      } else {
        const update = this._commercialContactBuilder.buildUpdateStatement(
          this.contact()!,
          dv
        );
        this.updateContact(update);
      }
    } else {
      this._logAndMessage.translateToErrorAlert({
        bodyKey: 'COMMON.MESSAGES.ERROR.INVALID_FORM',
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      });
    }
  }

  createContact(input: CommercialContactUpdateDto): void {
    this._commercialContactApi.createCommercialContact(input).subscribe({
      next: (contact) => {
        this.contact.set(contact);
        this._commercialContactBuilder.updateForm(
          this.form!,
          contact,
          this.canEdit()
        );
      },
      error: (error) => {
        this.saving.set(false);
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey:
            'COMMERCIAL_SERVICES.MESSAGES.ERROR.CREATE_COMMERCIAL_CONTACT',
        });
      },
      complete: () => {
        // Re-key the existing tab instead of replacing it, so nothing reloads.
        const contact = this.contact()!;
        this._tabService.updateTabId(this.id, contact.id);
        this.id = contact.id;
        this.setTabLabel(contact);
        this.updateActionsList();
        this.formDirty.set(false);
        this.changesEvent.emit({
          isDirty: false,
          index: this.index(),
          id: this.id,
        });
        this.saving.set(false);
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
        });
      },
    });
  }

  updateContact(update: CommercialContactUpdateDto): void {
    if (!this.canEdit()) {
      this._logAndMessage.error(
        'No Permissions',
        'User does not have permissions to update Power Desk contact.'
      );
      return;
    }

    this._commercialContactApi
      .updateCommercialContact(this.contact()!.id, update)
      .subscribe({
        next: (contact) => {
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
          });
          this.contact.set(contact);
          this._commercialContactBuilder.updateForm(
            this.form!,
            contact,
            this.canEdit()
          );
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey:
              'COMMERCIAL_SERVICES.MESSAGES.ERROR.UPDATE_COMMERCIAL_CONTACT',
          });
          this.saving.set(false);
        },
        complete: () => {
          this.formDirty.set(false);
          this.saving.set(false);
          this.changesEvent.emit({
            isDirty: false,
            index: this.index(),
            id: this.id,
          });
        },
      });
  }

  updateActionsList(): void {
    const contact = this.contact();
    if (!contact?.id || this.isHistoricalRecord() || contact.deletedAt) {
      this.actions.set([]);
      return;
    }

    const actions: SelectItem[] = [
      {
        label: this._translateService.instant('COMMON.LABEL.SELECT_ACTION'),
        value: null,
      },
    ];
    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.VIEW_COMMERCIAL_CONTACTS
      )
    ) {
      actions.push(
        {
          label: this._translateService.instant('COMMON.LABEL.VIEW_AUDIT_INFO'),
          value: VIEW_AUDIT,
        },
        {
          label: this._translateService.instant(
            'COMMON.LABEL.VIEW_HISTORICAL_RECORD'
          ),
          value: VIEW_HISTORICAL_RECORD,
        }
      );
    }
    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS
          .DELETE_COMMERCIAL_CONTACTS
      )
    ) {
      actions.push({
        label: this._translateService.instant(
          'COMMERCIAL_SERVICES.LABEL.DELETE_CONTACT'
        ),
        value: DELETE_COMMERCIAL_CONTACT,
      });
    }
    this.actions.set(actions);
  }

  actionEvent(event: SelectChangeEvent, object?: Select): void {
    const action = event.value;
    // Select.clear() re-emits onChange with null; ignore it to avoid recursion.
    if (!action) {
      return;
    }
    this.selectedAction.set(null);
    if (action === VIEW_AUDIT) {
      const history$ = this._commercialContactApi.getCommercialContactHistories(
        this.contact()!.id
      );
      this._dialogService.open(AuditDialogContainerComponent, {
        dismissableMask: true,
        modal: true,
        closable: true,
        width: '25%',
        data: { history: history$ },
      });
    } else if (action === VIEW_HISTORICAL_RECORD) {
      this.viewHistoricalRecord();
    } else if (action === DELETE_COMMERCIAL_CONTACT) {
      this.deleteCommercialContact();
    }
    object?.clear();
  }

  private viewHistoricalRecord(): void {
    const contact = this.contact()!;
    this._commercialContactApi
      .getCommercialContactHistories(contact.id)
      .subscribe({
        next: (histories) => {
          this._dialogService
            .open(HistoryVersionDialogComponent, {
              header: this._translateService.instant(
                'COMMON.LABEL.VIEW_HISTORICAL_RECORD'
              ),
              modal: true,
              closable: true,
              dismissableMask: true,
              data: {
                versions: histories.map((history) => ({
                  label: 'V' + history.version,
                  value: history.version,
                })),
              },
            })
            ?.onClose.subscribe((version?: number) => {
              if (version !== undefined) {
                this.openHistoricalVersion(contact.id, version);
              }
            });
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'COMMON.MESSAGES.ERROR.AUDIT_INFO',
          });
        },
      });
  }

  private openHistoricalVersion(contactId: string, version: number): void {
    this._commercialContactApi
      .getCommercialContact(contactId, version)
      .subscribe({
        next: (historicalContact) => {
          if (!historicalContact) {
            return;
          }
          const tab = this._tabService.buildNewTab(
            COMMERCIAL_CONTACT_DETAIL_CONTAINER,
            true,
            `${historicalContact.fullName}${historyVersionSuffix(version)}`,
            buildHistoryTabId(contactId, version)
          );
          this._tabService.openTab(tab);
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'COMMON.MESSAGES.ERROR.AUDIT_INFO',
          });
        },
      });
  }

  private deleteCommercialContact(): void {
    const contact = this.contact();
    if (!contact) {
      return;
    }

    this._confirmationService.confirm({
      header: this._translateService.instant(
        'COMMERCIAL_SERVICES.MESSAGES.HEADERS.DELETE_CONTACT'
      ),
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: this._translateService.instant('COMMON.LABEL.BUTTONS.YES'),
      rejectLabel: this._translateService.instant('COMMON.LABEL.BUTTONS.NO'),
      message: this._translateService.instant(
        'COMMERCIAL_SERVICES.MESSAGES.CONFIRMATION.DELETE_CONTACT'
      ),
      accept: () => {
        this.isLoading.set(true);
        this._commercialContactApi
          .deleteCommercialContact(contact.id)
          .subscribe({
            next: () => {
              // The tab closes on success, so no local state cleanup is needed.
              this._commercialContactTableService.setSelected(null);
              this._logAndMessage.translateToSuccessMessage({
                bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
                headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
              });
              if (this._deviceService.isMobile()) {
                const tab = this._tabService.buildNewTab(
                  COMMERCIAL_CONTACT_SEARCH_CONTAINER,
                  true
                );
                this._tabService.setMobileTab(tab);
              } else {
                this._tabService.closeTab(this._tabService.getActiveIndex());
              }
            },
            error: (error) => {
              console.error(error);
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                bodyKey:
                  'COMMERCIAL_SERVICES.MESSAGES.ERROR.UPDATE_COMMERCIAL_CONTACT',
              });
              this.isLoading.set(false);
            },
            complete: () => {
              this.isLoading.set(false);
            },
          });
      },
    });
  }
}
